/**
 * Объединение дублирующихся строк товаров по MaterialZid (ProductZid).
 * 
 * Вызывается перед сохранением документа (Черновик / В работу).
 * Суммирует количества (упак + шт), объединяет маркировочные коды.
 */

import { TableRowData } from './types';

function getSeriesKey(series: any): string {
    if (!series) return '';
    try {
        const parsed = typeof series === 'string' ? JSON.parse(series) : series;
        if (Array.isArray(parsed)) {
            const names = parsed
                .map((s: any) => (typeof s === 'string' ? s : (s?.name || '')))
                .filter(Boolean)
                .sort()
                .join(',');
            return names;
        }
    } catch {}
    return typeof series === 'string' ? series.trim() : '';
}

function getRowKey(row: TableRowData): string {
    if (!row.MaterialZid) return '';
    const seriesKey = getSeriesKey(row.series);
    return seriesKey ? `${row.MaterialZid}__${seriesKey}` : row.MaterialZid;
}

export function mergeDuplicateRows(rows: TableRowData[]): TableRowData[] {
    // Map: Key (MaterialZid + series) → массив индексов в rows
    const groups = new Map<string, number[]>();

    rows.forEach((row, index) => {
        const key = getRowKey(row);
        if (!key) return; // Пропускаем строки без MaterialZid (незаполненные)

        if (!groups.has(key)) {
            groups.set(key, []);
        }
        groups.get(key)!.push(index);
    });

    // Если нет дублей — возвращаем как есть
    const hasDuplicates = Array.from(groups.values()).some(indices => indices.length > 1);
    if (!hasDuplicates) {
        return rows;
    }

    // Собираем результат, сохраняя порядок (позиция первого вхождения)
    const mergedIndices = new Set<number>(); // Индексы строк, которые были поглощены
    const result: TableRowData[] = [];

    rows.forEach((row, index) => {
        if (mergedIndices.has(index)) return; // Уже была объединена с предыдущей

        const key = getRowKey(row);
        if (!key) {
            // Строка без MaterialZid — просто добавляем
            result.push(row);
            return;
        }

        const indices = groups.get(key)!;
        if (indices.length === 1) {
            // Уникальная строка — добавляем как есть
            result.push(row);
            return;
        }

        // Объединяем все строки с этим MaterialZid
        const rowsToMerge = indices.map(i => rows[i]);

        // Суммируем количества
        let totalQuantity = 0;
        let totalQuantityPcs = 0;
        const allMarkCodes: string[] = [];
        const allSeries: any[] = [];
        const comments: string[] = [];
        let hasPartialScanning = false;

        for (const r of rowsToMerge) {
            const qty = parseFloat(r.Quantity) || 0;
            const pcs = parseFloat(r.QuantityPcs || '0') || 0;
            totalQuantity += qty;
            totalQuantityPcs += pcs;

            // Собираем маркировочные коды
            if (r.markCodes) {
                const codes = Array.isArray(r.markCodes) ? r.markCodes : JSON.parse(r.markCodes || '[]');
                allMarkCodes.push(...codes.filter((c: string) => c.length > 0));
            }

            // Собираем серии
            if (r.series) {
                try {
                    const parsed = typeof r.series === 'string' ? JSON.parse(r.series) : r.series;
                    if (Array.isArray(parsed)) {
                        allSeries.push(...parsed);
                    }
                } catch {}
            }

            // Собираем комментарии
            if (r.Comment && r.Comment.trim()) {
                comments.push(r.Comment.trim());
            }

            if (r.partialScanning) {
                hasPartialScanning = true;
            }
        }

        // Создаём объединённую строку на базе первого вхождения
        const first = rowsToMerge[0];
        const mergedRow: TableRowData = {
            id: Date.now().toString() + Math.random().toString().slice(2),
            MaterialZid: first.MaterialZid,
            MaterialCode: first.MaterialCode,
            MaterialName: first.MaterialName,
            SerialNumber: first.SerialNumber,
            SerialDate: first.SerialDate,
            Quantity: totalQuantity > 0 ? totalQuantity.toString() : '',
            QuantityPcs: totalQuantityPcs > 0 ? totalQuantityPcs.toString() : '',
            Quantity1C: first.Quantity1C || '',
            Barcode: first.Barcode,
            Comment: comments.length > 0 ? comments.join('; ') : '',
            selectedMaterial: first.selectedMaterial,
            markCodes: allMarkCodes,
            partialScanning: hasPartialScanning,
            series: allSeries.length > 0 ? JSON.stringify(allSeries) : undefined,
        };

        result.push(mergedRow);

        // Помечаем все строки группы как обработанные
        for (const i of indices) {
            mergedIndices.add(i);
        }
    });

    console.log(`Объединение дублей: ${rows.length} строк → ${result.length} строк`);

    return result;
}
